import {
  enforceLenient,
  privilegesLenient,
  AbacReducedPolicy,
  AbacRuleComparison,
} from '@lifeomic/abac';

export interface Rule {
  [rule: string]: AbacRuleComparison;
}

export interface Policy {
  rules: Record<string, boolean | Rule[]>;
}

export const enforce = (
  operation: string,
  policy: AbacReducedPolicy,
  attributes?: object,
) => {
  if (!policy) {
    return false;
  } else {
    return enforceLenient(operation, policy, attributes || {});
  }
};

export const privileges: typeof privilegesLenient = (policy, attributes) => {
  if (!policy) {
    return [];
  } else {
    return privilegesLenient(policy, attributes);
  }
};

export const permissions = {
  accessAdmin: 'accessAdmin',
  billingAdmin: 'billingAdmin',
  projectAdmin: 'projectAdmin',
  accountAdmin: 'accountAdmin',
  ehrAdmin: 'ehrAdmin',
  createData: 'createData',
  readData: 'readData',
  readMaskedData: 'readMaskedData',
  updateData: 'updateData',
  deleteData: 'deleteData',
  downloadFile: 'downloadFile',
  layoutAdmin: 'layoutAdmin',
  apiKeyUser: 'apiKeyUser',
  ruleAdmin: 'ruleAdmin',
  publishContent: 'publishContent',
  inviteUsers: 'inviteUsers',
  developApps: 'developApps',
  ingestFromEHR: 'ingestFromEHR',
  fileAdmin: 'fileAdmin',
  workflowAdmin: 'workflowAdmin',
  createGenomicData: 'createGenomicData',
  clinicalTrialAlertUser: 'clinicalTrialAlertUser',
  createSubjectLayoutNote: 'createSubjectLayoutNote',
};

export const hasAccessToProject = (policy: Policy, project: any) => {
  const attributes = {
    resource: {
      dataset: project.id,
    },
  };

  return (
    enforce(permissions.readData, policy, attributes) ||
    enforce(permissions.readMaskedData, policy, attributes) ||
    enforce(permissions.downloadFile, policy, attributes)
  );
};

/**
 * Will check if the user has readMaskedData permission but not read data permission.
 * If a cohort ID is provided, it is possible that a user is mask forced for the project
 * but has readData for the active cohort and the user is not forced into masking.
 */
export const isMaskingForced = (
  policy: Policy,
  project: string,
  cohort?: string,
) => {
  // first check explicitely without cohorts
  const withoutCohortsAttributes = {
    resource: {
      dataset: project,
    },
  };

  const withUnspecifiedCohortAttributes = {
    resource: {
      dataset: project,
      cohorts: ['unspecified'],
      cohortAffinity: true,
    },
  };

  const forceMaskedForJustProject =
    enforce(permissions.readMaskedData, policy, withoutCohortsAttributes) &&
    !enforce(permissions.readData, policy, withoutCohortsAttributes);

  const forceMaskedForUnspecifiedCohort =
    enforce(
      permissions.readMaskedData,
      policy,
      withUnspecifiedCohortAttributes,
    ) &&
    !enforce(permissions.readData, policy, withUnspecifiedCohortAttributes);

  const projectLevelMaskForced =
    forceMaskedForJustProject || forceMaskedForUnspecifiedCohort;

  // if no cohort specified, no need to evaluate if user has cohort access
  if (!cohort) return projectLevelMaskForced;

  // if the user is not masked forced at the project level, then cohort check is irrelevant
  if (!projectLevelMaskForced) return false;

  const withCohortsAttributes = {
    resource: {
      dataset: project,
      cohorts: [cohort],
      cohortAffinity: true,
    },
  };

  return (
    enforce(permissions.readMaskedData, policy, withCohortsAttributes) &&
    !enforce(permissions.readData, policy, withCohortsAttributes)
  );
};
